import { createContext, type ReactNode, useContext } from 'react';

/**
 * Fixed UI words used inside components (Cancel, Close, …). The app provides them once from its
 * translations, so components stay language-agnostic and call sites do not repeat them.
 */
export interface UiStrings {
  cancel: string;
  close: string;
  save: string;
  loading: string;
  moreSections: string;
  previousPage: string;
  nextPage: string;
  /** e.g. "{from}–{to} of {total}" — receives the numbers already formatted. */
  pageRange: (from: string, to: string, total: string) => string;
  typeToConfirm: (word: string) => string;
}

const fallback: UiStrings = {
  cancel: 'Cancel',
  close: 'Close',
  save: 'Save',
  loading: 'Loading…',
  moreSections: 'More',
  previousPage: 'Previous',
  nextPage: 'Next',
  pageRange: (from, to, total) => `${from}–${to} / ${total}`,
  typeToConfirm: (word) => `Type ${word} to confirm`,
};

const Ctx = createContext<UiStrings>(fallback);

export function UiStringsProvider({ value, children }: { value: UiStrings; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useUiStrings = (): UiStrings => useContext(Ctx);
